import qs from 'qs';
import type {
    MakePurchaseOfferApiRequest,
    MakePurchaseOfferApiResponse,
    MarkAsPaidByBuyerApiRequest,
    MarkAsPaidByBuyerApiResponse,
    DeclinePurchaseOfferApiRequest,
    DeclinePurchaseOfferApiResponse,
    GetOffersBySellerAndBuyerAndItemApiResponse,
    OffersbysellerandbuyeranditemRequest,
    AcceptPurchaseOfferApiResponse,
    AcceptPurchaseOfferApiRequest,
    GetCommissionFeeForItemApiResponse,
    CommissionfeeforitemRequest,
    MarkItemAsUnavailableForPurchaseOfferApiRequest,
    MarkItemAsUnavailableForPurchaseOfferApiResponse,
    GetDiscountApiResponse,
    DiscountApiRequest,
    GetDiscountViewModelApiResponse,
    DiscountApiResponse,
    MakeCounterOfferApiRequest,
    MakeCounterOfferApiResponse,
    AcceptCounterOfferApiResponse,
    AcceptCounterOfferApiRequest,
    DeclineCounterOfferApiResponse,
    DeclineCounterOfferApiRequest
} from './types/webapi-shopping-generated';
import { webApi, webApiTags } from './webapi-api';

export const shoppingApi = webApi.injectEndpoints({
    endpoints: (builder) => ({
        getCommissionFeeForItem: builder.query<
            GetCommissionFeeForItemApiResponse,
            CommissionfeeforitemRequest
        >({
            query: (parameters) =>
                `/api/webapi/shopping/commissionfeeforitem?${qs.stringify(
                    parameters
                )}`
        }),
        purchaseOffer: builder.mutation<
            MakePurchaseOfferApiResponse,
            MakePurchaseOfferApiRequest
        >({
            query: (parameters) => {
                return {
                    url: '/api/webapi/shopping/offer/makepurchaseoffer',
                    method: 'POST',
                    body: parameters
                };
            },
            // This doesn't work right now, but would work if Next.js would use RTKQ to fetch data on initial page load
            invalidatesTags: (result, error, arg) => [
                { type: webApiTags.Item, id: arg.itemId }
            ]
        }),
        markAsPaid: builder.mutation<
            MarkAsPaidByBuyerApiResponse,
            MarkAsPaidByBuyerApiRequest
        >({
            query: ({ purchaseOrderId }) => ({
                url: `/api/webapi/shopping/payment/markaspaid`,
                method: 'POST',
                body: { purchaseOrderId }
            })
        }),
        declinePurchaseOffer: builder.mutation<
            DeclinePurchaseOfferApiResponse,
            DeclinePurchaseOfferApiRequest
        >({
            query: (parameters) => {
                return {
                    url: `/api/webapi/shopping/offer/declinepurchaseoffer`,
                    method: 'POST',
                    body: parameters
                };
            },
            invalidatesTags: (result, error, arg) =>
                !error ? [{ type: webApiTags.Offer, id: arg.itemId }] : []
        }),
        markAsUnavailable: builder.mutation<
            MarkItemAsUnavailableForPurchaseOfferApiResponse,
            MarkItemAsUnavailableForPurchaseOfferApiRequest
        >({
            query: ({ itemId }) => {
                return {
                    url: `/api/webapi/shopping/offer/markitemasunavailableforpurchaseoffer`,
                    method: 'POST',
                    body: { itemId }
                };
            },
            invalidatesTags: (result, error, arg) =>
                !error ? [{ type: webApiTags.Offer, id: arg.itemId }] : []
        }),
        acceptPurchaseOffer: builder.mutation<
            AcceptPurchaseOfferApiResponse,
            AcceptPurchaseOfferApiRequest
        >({
            query: (parameters) => {
                return {
                    url: `/api/webapi/shopping/offer/acceptpurchaseoffer`,
                    method: 'POST',
                    body: parameters
                };
            },
            invalidatesTags: (result, error, arg) =>
                !error ? [{ type: webApiTags.Offer, id: arg.itemId }] : []
        }),
        makeCounterOffer: builder.mutation<
            MakeCounterOfferApiResponse,
            MakeCounterOfferApiRequest
        >({
            query: (parameters) => {
                return {
                    url: `/api/webapi/shopping/offer/makecounteroffer`,
                    method: 'POST',
                    body: parameters
                };
            },
            invalidatesTags: (result, error, arg) =>
                !error ? [{ type: webApiTags.Offer, id: arg.itemId }] : []
        }),
        declineCounterOffer: builder.mutation<
            DeclineCounterOfferApiResponse,
            DeclineCounterOfferApiRequest
        >({
            query: (parameters) => {
                return {
                    url: `/api/webapi/shopping/offer/declinecounteroffer`,
                    method: 'POST',
                    body: parameters
                };
            },
            invalidatesTags: (result, error, arg) =>
                !error ? [{ type: webApiTags.Offer, id: arg.itemId }] : []
        }),
        acceptCounterOffer: builder.mutation<
            AcceptCounterOfferApiResponse,
            AcceptCounterOfferApiRequest & { itemId: number }
        >({
            query: (parameters) => {
                return {
                    url: `/api/webapi/shopping/offer/acceptcounteroffer`,
                    method: 'POST',
                    body: parameters
                };
            },
            invalidatesTags: (result, error, arg) =>
                !error ? [{ type: webApiTags.Offer, id: arg.itemId }] : []
        }),
        getOffersBySellerAndBuyerAndItem: builder.query<
            GetOffersBySellerAndBuyerAndItemApiResponse,
            OffersbysellerandbuyeranditemRequest
        >({
            query: (parameters) => {
                return {
                    url: `/api/webapi/shopping/offer/offersbysellerandbuyeranditem?${qs.stringify(
                        parameters
                    )}`,
                    method: 'GET'
                };
            },
            providesTags: (result) =>
                result?.purchaseOffers
                    ? [
                          ...result.purchaseOffers.map((purchaseOffer) => ({
                              type: webApiTags.Offer,
                              id: purchaseOffer.itemId
                          })),
                          webApiTags.Offer
                      ]
                    : [webApiTags.Offer]
        }),
        getDiscount: builder.query<GetDiscountViewModelApiResponse, string>({
            query: (purchaseOrderId) =>
                `/api/webapi/shopping/payment/discountviewmodel?purchaseOrderId=${purchaseOrderId}`
        }),
        getDiscountStatus: builder.query<GetDiscountApiResponse, number>({
            query: (discountId) =>
                `/api/webapi/shopping/payment/discount?discountId=${discountId}`
        }),
        setDiscount: builder.mutation<DiscountApiResponse, DiscountApiRequest>({
            query: (payload) => ({
                url: '/api/webapi/shopping/payment/discount',
                method: 'POST',
                body: payload
            })
        })
    })
});

export const {
    usePurchaseOfferMutation,
    useMarkAsPaidMutation,
    useDeclinePurchaseOfferMutation,
    useAcceptPurchaseOfferMutation,
    useGetOffersBySellerAndBuyerAndItemQuery,
    useGetCommissionFeeForItemQuery,
    useMarkAsUnavailableMutation,
    useGetDiscountQuery,
    useGetDiscountStatusQuery,
    useSetDiscountMutation,
    useMakeCounterOfferMutation,
    useDeclineCounterOfferMutation,
    useAcceptCounterOfferMutation
} = shoppingApi;
